import React, { useState, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Grid2,
  ListItemIcon,
  MenuItem,
  Menu,
  useTheme,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SubtitlesIcon from "@mui/icons-material/Subtitles";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import { useTranslation } from "react-i18next";
import { hexToRgba } from "@/Components/helpers/util";
import AttachmentLoader from "./AttachmentLoader";
import { toast } from "react-toastify";
import { useGetAttachmentUrlMutation } from "@/features/Global/api";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

interface AttachmentGridViewProps {
  attachments: any[];
  isLoading: boolean;
  onView: (attachment: any) => void;
  onEdit: (attachment: any) => void;
  onDelete: (attachment: any) => void;
  anchorEl: HTMLElement | null;
  selectedRowForMenu: any;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, row: any) => void;
  onMenuClose: () => void;
  onFileUpload: (files: File[]) => void;
  isDropZone?: boolean;
  onTranscribe?: (fileId: string, fileName: string) => void;
  transcribingVideos?: Set<string>;
  transcriptionProgress?: Map<string, string>;
}

const AttachmentCard = ({
  attachment,
  onView,
  onMenuOpen,
  theme,
  t,
  getFileIcon,
  AttachmentLoader,
  onTranscribe,
  transcribingVideos,
  transcriptionProgress,
}) => {
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);

  const isImage =
    attachment.fileType?.startsWith("image/") ||
    attachment.type === "screenshot";
  const isVideo =
    attachment.type === "video" || attachment.fileType?.startsWith("video/");

  const needsFetch =
    isImage && attachment.url && !attachment.url.startsWith("data:");

  const handleVideoLoad = () => {
    setVideoLoading(false);
    setVideoError(false);
  };

  const handleVideoError = () => {
    setVideoLoading(false);
    setVideoError(true);
  };

  return (
    <Card
      sx={{
        height: 240,
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        transition: "all 0.2s ease-in-out",
        border: `1px solid ${theme.palette.divider}`,
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: theme.shadows[4],
          borderColor: theme.palette.primary.main,
        },
      }}
      onClick={() => onView(attachment)}
    >
      <Box sx={{ position: "relative", height: 160, overflow: "hidden" }}>
        {isVideo ? (
          <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
            {/* Video loading indicator */}
            {videoLoading && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.default : "#f5f5f5",
                  zIndex: 1,
                }}
              >
                <CircularProgress size={24} />
              </Box>
            )}

            {/* Video element */}
            <video
              src={attachment.url}
              style={{
                width: "100%",
                height: "160px",
                objectFit: "cover",
                backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.default : "#f5f5f5",
                display: videoError ? "none" : "block",
              }}
              muted
              onLoadedData={handleVideoLoad}
              onCanPlay={handleVideoLoad}
              onError={handleVideoError}
            />

            {/* Video error fallback */}
            {videoError && (
              <Box
                sx={{
                  height: 160,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.default : "#f5f5f5",
                  color: theme.palette.text.secondary,
                }}
              >
                <VideoFileIcon sx={{ fontSize: 36 }} />
              </Box>
            )}
          </Box>
        ) : isImage ? (
          <AttachmentLoader
            attachment={attachment}
            isImage={isImage}
            needsFetch={needsFetch}
          />
        ) : (
          <Box
            sx={{
              height: 160,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.default : "#f5f5f5",
              color: theme.palette.text.secondary,
            }}
          >
            {getFileIcon(attachment, 36)}
          </Box>
        )}

        {/* Loading indicator for uploads */}
        {attachment.isUploading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: theme.palette.mode === "dark" ? "rgba(30, 30, 30, 0.8)" : "rgba(255, 255, 255, 0.8)",
              zIndex: 2,
            }}
          >
            <CircularProgress size={24} />
          </Box>
        )}

        {/* Type badge */}
        <Chip
          label={attachment.type}
          size="small"
          color={
            attachment.type === "screenshot"
              ? "primary"
              : attachment.type === "video"
                ? "secondary"
                : "default"
          }
          sx={{
            position: "absolute",
            top: 6,
            left: 6,
            fontSize: "9px",
            height: 18,
            textTransform: "capitalize",
            zIndex: 1,
          }}
        />

        {/* Annotation badge */}
        {attachment.isAnnotated && !attachment.isUploading && (
          <Chip
            label={t("Annotated")}
            size="small"
            sx={{
              position: "absolute",
              top: 6,
              right: 32,
              fontSize: "8px",
              height: 16,
              color: "primary.main",
              bgcolor: hexToRgba(theme.palette.primary.main, 0.1),
              zIndex: 1,
            }}
          />
        )}

        {/* Upload failed indicator */}
        {attachment.uploadFailed && (
          <Chip
            label={t("Upload Failed")}
            size="small"
            sx={{
              position: "absolute",
              bottom: 6,
              left: 6,
              fontSize: "8px",
              height: 16,
              color: "error.main",
              bgcolor: hexToRgba(theme.palette.error.main, 0.1),
              zIndex: 1,
            }}
          />
        )}

        {/* Transcribe button for videos */}
        {isVideo && onTranscribe && (() => {
          const id = attachment.fileId || attachment.fileName || attachment.name;
          const progress = transcriptionProgress?.get(id);
          const isTranscribing = transcribingVideos?.has(id);
          const isComplete = progress?.includes("✓");

          return (
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                onTranscribe(id, attachment.fileName || attachment.name);
              }}
              disabled={isTranscribing}
              sx={{
                position: "absolute",
                top: 6,
                right: 46,
                backgroundColor: (theme) => {
                  if (isComplete) {
                    return "rgba(76, 175, 80, 0.9)"; // Light green for completed
                  } else if (isTranscribing) {
                    return `${theme.palette.primary.main}1a`; // Primary color with 0.1 opacity when transcribing
                  }
                  return theme.palette.primary.main; // Primary color default
                },
                color: isTranscribing ? "primary.main" : "white",
                zIndex: 1,
                "&:hover": {
                  backgroundColor: (theme) => {
                    if (isComplete) {
                      return "rgba(76, 175, 80, 1)";
                    } else if (isTranscribing) {
                      return `${theme.palette.primary.main}33`;
                    }
                    return theme.palette.primary.dark;
                  },
                },
                "&:disabled": {
                  backgroundColor: (theme) => `${theme.palette.primary.main}1a`,
                  color: "primary.main",
                },
              }}
              title={
                isTranscribing
                  ? t("Transcribing...")
                  : isComplete
                  ? t("Transcription complete")
                  : t("Generate text from video")
              }
            >
              {isTranscribing ? (
                <CircularProgress size={16} thickness={4} sx={{ color: "primary.main" }} />
              ) : (
                <SubtitlesIcon fontSize="small" />
              )}
            </IconButton>
          );
        })()}

        {/* Actions menu button */}
        <IconButton
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            onMenuOpen(event, attachment);
          }}
          sx={{
            position: "absolute",
            top: 6,
            right: 6,
            backgroundColor: theme.palette.mode === "dark" ? "rgba(30, 30, 30, 0.9)" : "rgba(255, 255, 255, 0.9)",
            zIndex: 1,
            "&:hover": {
              backgroundColor: theme.palette.mode === "dark" ? "rgba(30, 30, 30, 1)" : "rgba(255, 255, 255, 1)",
            },
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Box>

      <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            mb: 0.5,
          }}
          title={attachment.name}
        >
          {attachment.name}
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: 11 }}
          >
            {attachment.date
              ? new Date(attachment.date).toLocaleDateString()
              : "N/A"}
          </Typography>
          {attachment.size && attachment.size !== "N/A" && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: 11 }}
            >
              {attachment.size}
            </Typography>
          )}
        </Box>

        {/* Upload status indicator */}
        {attachment.isUploading && (
          <Typography
            variant="caption"
            color="info.main"
            sx={{ display: "block", mt: 0.5, fontSize: 11 }}
          >
            {t("Uploading...")}
          </Typography>
        )}

        {attachment.uploadFailed && (
          <Typography
            variant="caption"
            color="error.main"
            sx={{ display: "block", mt: 0.5, fontSize: 11 }}
          >
            {t("Upload failed, using local version")}
          </Typography>
        )}

        {/* Transcription progress indicator */}
        {(() => {
          const id = attachment.fileId || attachment.fileName || attachment.name;
          const progress = transcriptionProgress?.get(id);
          if (progress) {
            const isComplete = progress.includes("✓");
            const isFailed = progress.includes("✗");
            const isInProgress = !isComplete && !isFailed;
            return (
              <Typography
                variant="caption"
                color={isComplete ? "success.main" : isFailed ? "error.main" : "primary.main"}
                sx={(theme) => ({
                  display: "inline-block",
                  mt: 0.5,
                  fontSize: 11,
                  fontWeight: 500,
                  px: 1,
                  py: 0.5,
                  borderRadius: 0.5,
                  backgroundColor: isInProgress ? `${theme.palette.primary.main}1a` : "transparent"
                })}
              >
                {progress}
              </Typography>
            );
          }
          return null;
        })()}
      </CardContent>
    </Card>
  );
};

const AttachmentGridView: React.FC<AttachmentGridViewProps> = ({
  attachments,
  isLoading,
  onView,
  onEdit,
  onDelete,
  anchorEl,
  selectedRowForMenu,
  onMenuOpen,
  onMenuClose,
  onFileUpload,
  isDropZone,
  onTranscribe,
  transcribingVideos,
  transcriptionProgress,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();
  const [downloadingItems, setDownloadingItems] = useState<Set<string>>(
    new Set(),
  );
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        onFileUpload(files);
      }
    },
    [onFileUpload],
  );

  const handleBrowseFiles = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;
    input.accept = "image/*";
    input.onchange = (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || []);
      if (files.length > 0) {
        onFileUpload(files);
      }
    };
    input.click();
  };

  const handleMenuAction = (action: string) => {
    if (!selectedRowForMenu) return;

    switch (action) {
      case "view":
        onView(selectedRowForMenu);
        break;
      case "edit":
        onEdit(selectedRowForMenu);
        break;
      case "delete":
        onDelete(selectedRowForMenu);
        break;
      case "download":
        handleDownload(selectedRowForMenu);
        break;
    }
    onMenuClose();
  };
  const downloadBase64File = (base64Data: string, fileName: string) => {
    try {
      const link = document.createElement("a");
      link.href = base64Data;
      link.download = fileName || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading base64 file:", error);
      toast.error(t("Failed to download file"));
    }
  };

  const downloadFromUrl = async (url: string, fileName: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading from URL:", error);
      toast.error(t("Failed to download file"));
    }
  };
  const [postAttachmentKey, { isLoading: isGettingUrlLoading }] =
    useGetAttachmentUrlMutation();
  const handleDownload = async (attachment: any) => {
    const itemId = attachment._id;
    setDownloadingItems((prev) => new Set(prev).add(itemId));

    try {
      const fileName = attachment.name || attachment.fileName || "download";
      // Case 1: Base64 data (local files or annotated images)
      if (attachment.url && attachment.url.startsWith("data")) {
        downloadBase64File(attachment.url, fileName);
        toast.success(t("File downloaded successfully"));
        return;
      }

      // Case 2: Direct URL (video recordings, external files)
      if (
        attachment.url &&
        (attachment.url.startsWith("http") ||
          attachment.url.startsWith("blob:"))
      ) {
        await downloadFromUrl(attachment.url, fileName);
        toast.success(t("File downloaded successfully"));
        return;
      }
      if (attachment.url && attachment?.url.startsWith("dev/")) {
        const keyToFetch = attachment.serverUrl || attachment.url;

        const response = await postAttachmentKey({
          key: keyToFetch,
        }).unwrap();

        if (response?.data?.decryptedData?.url) {
          await downloadFromUrl(response.data.decryptedData.url, fileName);
          toast.success(t("File downloaded successfully"));
        } else {
          throw new Error("Failed to get download URL");
        }
        return;
      }
      toast.error(t("No download URL available for this file"));
    } catch (error) {
      console.error("Error downloading attachment:", error);
      toast.error(t("Failed to download file"));
    } finally {
      setDownloadingItems((prev) => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };
  const getFileIcon = (attachment: any, size: number = 48) => {
    const iconProps = {
      sx: { fontSize: size, color: theme.palette.text.secondary },
    };

    if (
      attachment.type === "video" ||
      attachment.fileType?.startsWith("video/")
    ) {
      return <VideoFileIcon {...iconProps} />;
    } else {
      return <InsertDriveFileIcon {...iconProps} />;
    }
  };

  return (
    <>
      <Box
        sx={{
          maxHeight: 600,
          overflow: "auto",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          p: 1.5,
          backgroundColor: theme.palette.mode === "dark" ? theme.palette.background.paper : "#fafafa",
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Grid2 container spacing={1.5}>
            {/* Drop Zone Card - Always first */}
            {isDropZone && <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              <Card
                sx={{
                  height: 240,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  border: `2px dashed ${isDragOver ? theme.palette.primary.main : theme.palette.divider}`,
                  backgroundColor: isDragOver
                    ? `${theme.palette.primary.main}08`
                    : "transparent",
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: `${theme.palette.primary.main}08`,
                    transform: "translateY(-2px)",
                    boxShadow: theme.shadows[4],
                  },
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleBrowseFiles}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    color: isDragOver ? theme.palette.primary.main : theme.palette.text.secondary,
                  }}
                >
                  <CloudUploadIcon
                    sx={{
                      fontSize: 36,
                      mb: 1,
                      color: "inherit",
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "inherit",
                      fontWeight: 500,
                      textAlign: "center",
                      mb: 0.5,
                    }}
                  >
                    {isDragOver
                      ? t("Drop files here")
                      : t("Drop files to attach")}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "inherit",
                      fontWeight: 600,
                      textAlign: "center",
                      mb: 0.5,
                    }}
                  >
                    {t("or Browse")}
                  </Typography>
                </Box>
              </Card>
            </Grid2>}

            {/* Existing attachments */}
            {attachments.map((attachment, index) => (
              <Grid2
                key={attachment._id}
                size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
              >
                <AttachmentCard
                  attachment={attachment}
                  onView={onView}
                  onMenuOpen={onMenuOpen}
                  theme={theme}
                  t={t}
                  getFileIcon={getFileIcon}
                  AttachmentLoader={AttachmentLoader}
                  onTranscribe={onTranscribe}
                  transcribingVideos={transcribingVideos}
                  transcriptionProgress={transcriptionProgress}
                />
              </Grid2>
            ))}
          </Grid2>
        )}
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          sx: {
            minWidth: 120,
            boxShadow: theme.shadows[8],
          },
        }}
      >
        <MenuItem
          onClick={() => handleMenuAction("view")}
          sx={{ fontSize: "14px" }}
        >
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          {t("View")}
        </MenuItem>
        <MenuItem
          onClick={() => handleMenuAction("download")}
          disabled={downloadingItems.has(selectedRowForMenu?._id)}
          sx={{ fontSize: "14px" }}
        >
          <ListItemIcon>
            {downloadingItems.has(selectedRowForMenu?._id) ? (
              <CircularProgress size={16} />
            ) : (
              <DownloadIcon fontSize="small" />
            )}
          </ListItemIcon>
          {downloadingItems.has(selectedRowForMenu?._id)
            ? t("Downloading...")
            : t("Download")}
        </MenuItem>
        {(selectedRowForMenu?.fileType?.startsWith("image/") ||
          selectedRowForMenu?.type === "screenshot") && (
          <MenuItem
            onClick={() => handleMenuAction("edit")}
            disabled={selectedRowForMenu?.isUploading}
            sx={{ fontSize: "14px" }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            {t("Edit")}
          </MenuItem>
        )}
        <MenuItem
          onClick={() => handleMenuAction("delete")}
          sx={{
            fontSize: "14px",
            color: theme.palette.error.main,
            "&:hover": {
              backgroundColor: hexToRgba(theme.palette.error.main, 0.1),
            },
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          {t("Delete")}
        </MenuItem>
      </Menu>
    </>
  );
};

export default AttachmentGridView;
